import { Box, ClickAwayListener, Fade, Popper, styled } from '@mui/material';
import { ClockIcon } from '@synergy/theme-next';
import { FilterParams } from '@synergy/models';
import { FocusEvent, KeyboardEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { PoolLink, useComponentSize } from '@synergy/components-next';
import { useNavigate } from 'react-router-dom';

import * as SearchHistoryActions from '../../../../../store/search-history-actions';
import { ROUTES } from '../../../../../utils/routes';
import { SearchAutocompleteContent } from './search-autocomplete-content';
import { SearchAutocompleteList } from './search-auotcomplete-list';
import { SearchInput, SearchInputProps } from './search-input';
import { SearchResultItem, useAutocompleteSearch } from './search-autocomplete-hook';
import { openInquiryModal } from '../../../../../store/single-inquiry-actions';
import { searchHistorySelector } from '../../../../../store/search-history-selectors';
import { useAppDispatch, useAppSelector } from '../../../../../../store/store';

const StyledPopper = styled(Popper)(({ theme }) => ({
    zIndex: theme.zIndex.modal,
}));

const PopupContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 0),
    marginTop: theme.spacing(0.5),
    background: theme.palette.background.paper,
    boxShadow: '0px 0px 9px rgba(0, 0, 0, 0.213853), 0px 2px 2px rgba(0, 0, 0, 0.144361)',
    borderRadius: 4,
}));

export const SearchAutocomplete = (props: SearchInputProps): ReactElement | null => {
    const { value, sx, ...inputProps } = props;

    const { measuredRef, width } = useComponentSize();
    const dispatch = useAppDispatch();
    const sizeRef = useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = useState(false);
    const [hasFocus, setHasFocus] = useState(false);
    const searchResult = useAutocompleteSearch(value);
    const searchHistory = useAppSelector(searchHistorySelector);
    const navigate = useNavigate();

    useEffect(() => {
        if (sizeRef.current) {
            measuredRef(sizeRef.current);
        }
    }, [measuredRef]);

    useEffect(() => {
        setOpen(hasFocus);
        if ((value?.length ?? 0) < 3 && searchHistory.length === 0) {
            dispatch(SearchHistoryActions.fetchSearchHistory());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, searchResult, hasFocus, dispatch]);

    const handleFocusChange = (e: FocusEvent<HTMLInputElement>): void => {
        setHasFocus(!hasFocus);
        hasFocus ? props.onBlur?.(e) : props.onFocus?.(e);
    };

    const handleSingleInquiry = (): void => {
        dispatch(openInquiryModal({ items: [{ id: 1, articleName: value ?? '', quantity: null }] }));
        setOpen(false);
    };

    const handleSearchCommit = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (event.key === 'Enter') {
            setOpen(false);
            (event.target as HTMLInputElement)?.blur();
            props.onKeyUp?.(event);
        }
    };

    const handleLastSearchedClick = (item: SearchResultItem): void => {
        navigate({
            pathname: ROUTES.searchPage,
            search: new URLSearchParams({
                [FilterParams.Search]: item.name,
            }).toString(),
        });
        setOpen(false);
    };

    const handleClearSearchHistory = (): void => {
        dispatch(SearchHistoryActions.clearSearchHistory());
    };

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={sizeRef} sx={sx}>
                <SearchInput
                    {...inputProps}
                    fullWidth={true}
                    value={value}
                    onFocus={handleFocusChange}
                    onBlur={handleFocusChange}
                    onKeyUp={handleSearchCommit}
                    data-testid="search-autocomplete"
                />
                {((value?.length ?? 0) > 2 || searchHistory.length > 0) && (
                    <StyledPopper open={open} anchorEl={sizeRef.current} sx={{ width }} transition={true}>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps}>
                                <PopupContent>
                                    {searchResult && searchResult.articles.length > 0 ? (
                                        <SearchAutocompleteContent
                                            searchResult={searchResult}
                                            search={value}
                                            onClose={() => setOpen(false)}
                                        />
                                    ) : (value?.length ?? 0) < 3 && searchHistory.length > 0 ? (
                                        <SearchAutocompleteList
                                            items={searchHistory}
                                            title={<FormattedMessage id="app.searchBar.dropdown.lastSearched" />}
                                            actionLabel={
                                                <FormattedMessage id="app.searchBar.dropdown.deleteSearchHistory" />
                                            }
                                            onItemClick={handleLastSearchedClick}
                                            onActionClick={handleClearSearchHistory}
                                            search={value}
                                            count={searchHistory.length}
                                            icon={<ClockIcon />}
                                        />
                                    ) : (
                                        <Box sx={{ px: 6 }}>
                                            <FormattedMessage id="app.searchBar.dropdown.empty" />{' '}
                                            <PoolLink colorVariant="primary2" onClick={handleSingleInquiry}>
                                                <FormattedMessage id="app.searchBar.dropdown.emptyInquiry" />
                                            </PoolLink>
                                        </Box>
                                    )}
                                </PopupContent>
                            </Fade>
                        )}
                    </StyledPopper>
                )}
            </Box>
        </ClickAwayListener>
    );
};
